import { atom, selector } from 'recoil'
import { keyFactory } from 'src/utils/recoil'
import { centroid } from '@turf/turf'
import { Position } from '@turf/helpers'
import { Feature, Polygon, MultiPolygon, YYYYMMDDDateString } from 'src/common-services'
import { MapShapeProps } from 'src/types/map-editor'
import { v4 as uuid } from 'uuid'

import * as searchAtoms from 'src/atoms/search'
import * as customAreaAtoms from 'src/atoms/custom-area'
import { Colors } from 'src/components/theme'
import { getPolygonFeature } from 'src/components/insights/custom-polygon-visits/utils'

const key = keyFactory('custom-polygon')

export type CustomFootFallDateRange = {
  start: YYYYMMDDDateString
  end: YYYYMMDDDateString
}

export const polygonFromCustomArea = selector({
  key: key('polygon-from-custom-area'),
  get: ({ get }) => {
    const polygonArea = get(customAreaAtoms.polygon)
    if (polygonArea) {
      return getPolygonFeature(polygonArea, Colors.buttonStandard)
    }

    const circleFeature = get(customAreaAtoms.circleFeature)
    if (circleFeature) {
      return getPolygonFeature(circleFeature.geometry, Colors.buttonStandard, 'custom-area', circleFeature.properties)
    }
    return undefined
  },
})

// Unique ID for the polygon either the place_id or the centroid of the custom area
export const polygonId = selector<string | undefined>({
  key: key('custom-polygon-id'),
  get: ({ get }) => {
    // Place
    const place = get(searchAtoms.googlePlace)
    // Custom area
    const polyFromCustomArea = get(polygonFromCustomArea)
    const polyCentroid = polyFromCustomArea ? centroid(polyFromCustomArea).geometry.coordinates.join() : undefined
    const polygonId = polyCentroid ? polyCentroid : place ? place.place_id : undefined
    return polygonId
  },
})

// Some name for the polygon, either the place name or a uuid
export const polygonName = selector<string | undefined>({
  key: key('custom-polygon-name'),
  get: ({ get }) => {
    const place = get(searchAtoms.googlePlace)
    const polygonName = place ? place.name : uuid()
    return polygonName
  },
})

export const polygonType = selector<'place' | 'custom-area' | undefined>({
  key: key('custom-polygon-type'),
  get: ({ get }) => {
    const polyId = get(polygonId)
    const place = get(searchAtoms.googlePlace)

    if (!polyId) {
      return undefined
    }

    if (polyId && !place) {
      return 'custom-area'
    }
    return 'place'
  },
})

export const polygonCenter = selector<Position | undefined>({
  key: key('location-center'),
  get: ({ get }) => {
    // Place
    const place = get(searchAtoms.googlePlace)
    if (place) {
      return place.geometry.location
    }
    // Custom Polygon
    const poly = get(polygon)
    if (poly && poly.geometry) {
      const center = centroid(poly.geometry).geometry.coordinates
      return center
    }
    // Custom Area - Polygon
    const customAreaPoly = get(customAreaAtoms.polygon)
    if (customAreaPoly) {
      const center = centroid(customAreaPoly).geometry.coordinates
      return center
    }
    // Custom Area - Radius
    const customRadius = get(customAreaAtoms.circle)
    if (customRadius) {
      return customRadius.center
    }
    return undefined
  },
})

export type CustomPolygonReportType = 'VISITATION' | 'TTA' | 'RANK'
export const sectionType = atom<CustomPolygonReportType>({
  key: key('section-type'),
  default: 'VISITATION',
})

export const checkExistingPolygon = atom<boolean>({
  key: key('check-existing-polygon'),
  default: false,
})
export const polygonExists = atom<boolean>({
  key: key('polygon-exists'),
  default: false,
})

export const hasExistingPolygon = selector<boolean>({
  key: key('has-existing-polygon'),
  get: ({ get }) => {
    return get(checkExistingPolygon) && get(polygonExists)
  },
})

export const polygon = atom<Feature<Polygon | MultiPolygon, MapShapeProps> | undefined>({
  key: key('polygon'),
  default: undefined,
})

export const multipolygon = selector<MultiPolygon | undefined>({
  key: key('multipolygon'),
  get: ({ get }) => {
    const poly = get(polygon)?.geometry
    if (!poly) {
      return undefined
    }

    const multipolygon: MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: [],
    }
    multipolygon.coordinates = poly.type === 'MultiPolygon' ? poly.coordinates : [poly.coordinates]
    return multipolygon
  },
})

export const polygonError = atom<
  | {
      message: string
      additionalInfo?: string
    }
  | undefined
>({
  key: key('polygon-error'),
  default: undefined,
})

export const canSubmit = selector({
  key: key('can-submit'),
  get: ({ get }) => {
    return Boolean(get(polygon) && get(multipolygon))
  },
})

export const makeSubmission = atom<boolean>({
  key: key('make-submission'),
  default: false,
})

export const modalOpen = atom<boolean>({
  key: key('modal-open'),
  default: false,
})
